import gql from "graphql-tag";
import ApolloClient from "@/@core/graphql";
import { Worker } from "../interfaces/Worker";
import { IndividualCareProvider } from "../interfaces/IndividualCareProvider";
import { Period } from "../interfaces/Period";
import { FinancialFlow, FinancialFlowState } from "../interfaces/FinancialFlow";
import { RunRef } from "../interfaces/RunRef";

export class GraphqlService {
  public static async getWorker(ssin: string): Promise<Worker> {
    const apolloClient = new ApolloClient({
      uri: `${process.env.BASE_URL}api/graphql/graphql`,
    });

    return (
      await apolloClient.query({
        query: gql`
          query getWorker($ssin: String!) {
            worker(ssin: $ssin) {
              nihii
              cbe
              nativeWorker {
                firstName
                lastName
                ssin
              }
              foreignBankAccount {
                iban
              }
              address {
                streetName
                streetNr
                streetBox
                postalCode
                municipality
              }
              claims {
                id
                state
                amount
                balance
              }
              workerVCs {
                exercise
                financialFlows {
                  id
                  cashingDate
                  paymentNr
                  amount
                  state
                  foreignBankAccount {
                    iban
                  }
                  communication
                  returnMotivation
                  liquidation {
                    id
                  }
                }
              }
            }
          }
        `,
        variables: {
          ssin: ssin,
        },
      })
    ).data.worker;
  }

  public static async getIndividualCareProviders(ssin: string, periodCode: string): Promise<IndividualCareProvider[]> {
    const apolloClient = new ApolloClient({
      uri: `${process.env.BASE_URL}api/graphql/graphql`,
    });

    return (
      await apolloClient.query({
        query: gql`
          query getIndividualCareProviders($ssin: String, $periodCode: String) {
            individualCareProviders(ssin: $ssin) {
              profession
              nihdi
              nihdiQualification
              encounters(periodCode: $periodCode) {
                count
                month
                run
                type
              }
              forfait(periodCode: $periodCode) {
                month
                run
              }
            }
          }
        `,
        variables: {
          ssin: ssin,
          periodCode: periodCode,
        },
      })
    ).data.individualCareProviders;
  }

  public static async getPeriods(): Promise<Period[]> {
    const apolloClient = new ApolloClient({
      uri: `${process.env.BASE_URL}api/graphql/graphql`,
    });

    return (
      await apolloClient.query({
        query: gql`
          query getPeriods {
            periods {
              code
              months {
                month
                year
              }
              runs {
                nr
                runInfos {
                  runType
                  liquidations {
                    id
                  }
                }
              }
            }
          }
        `,
      })
    ).data.periods;
  }

  public static async getSendConfig(
    origin: string | null = null,
    run: RunRef | null = null,
    flow: string | null = null
  ): Promise<FinancialFlow[]> {
    const apolloClient = new ApolloClient({
      uri: `${process.env.BASE_URL}api/graphql/graphql`,
    });

    return (
      await apolloClient.query({
        query: gql`
          query sendConfigs($origin: Origin, $run: RunRefInput) {
            sendConfigs(origin: $origin, run: $run) {
              origin
              run {
                nr
                of {
                  code
                }
              }
              flow
              send
              flowMessageCount
              recordCound
              doubleRecordCount
              use
            }
          }
        `,
        variables: {
          origin: origin,
          flow: flow,
          run: run,
        },
      })
    ).data.sendConfigs;
  }

  public static async updateSendConfig(send: any | null = null): Promise<FinancialFlow[]> {
    const apolloClient = new ApolloClient({
      uri: `${process.env.BASE_URL}api/graphql/graphql`,
    });

    return (
      await apolloClient.mutate({
        mutation: gql`
          mutation updateSendConfig($send: SendConfigRef!) {
            updateSendConfig(send: $send) {
              origin
              run {
                nr
                of {
                  code
                }
              }
              flow
              send
              flowMessageCount
              recordCound
              doubleRecordCount
              use
            }
          }
        `,
        variables: {
          send: send,
        },
      })
    ).data.sendConfigs;
  }

  public static async getFinancialFlows(
    ssin: string | null = null,
    cbe: string | null = null,
    nihii: string | null = null,
    state: FinancialFlowState[] | null = null,
    iban: string | null = null,
    cashingDateFrom: Date | null = null,
    cashingDateTo: Date | null = null,
    first = -1,
    offset = 0
  ): Promise<FinancialFlow[]> {
    const apolloClient = new ApolloClient({
      uri: `${process.env.BASE_URL}api/graphql/graphql`,
    });

    return (
      await apolloClient.query({
        query: gql`
          query getFinancialFlows(
            $ssin: String
            $cbe: String
            $nihii: String
            $state: [State]
            $bankAccount: String
            $cashingDateFrom: DateTime
            $cashingDateTo: DateTime
            $first: Int!
            $offset: Int!
          ) {
            financialFlows(
              ssin: $ssin
              cbe: $cbe
              nihii: $nihii
              state: $state
              bankAccount: $bankAccount
              cashingDateFrom: $cashingDateFrom
              cashingDateTo: $cashingDateTo
              first: $first
              offset: $offset
            ) {
              id
              communication
              cashingDate
              amount
              paymentNr
              communication
              state
              returnMotivation
              foreignBankAccount {
                iban
              }
              nativeWorker {
                firstName
                lastName
                cBE
              }
              workerVC {
                exercise
                worker {
                  cbe
                  nihii
                  nativeWorker {
                    firstName
                    lastName
                    ssin
                  }
                }
              }
            }
          }
        `,
        variables: {
          ssin: ssin,
          cbe: cbe,
          nihii: nihii,
          state: state,
          bankAccount: iban,
          cashingDateFrom: cashingDateFrom ? GraphqlService.getDateAsGraphQLString(cashingDateFrom) : cashingDateFrom,
          cashingDateTo: cashingDateTo ? GraphqlService.getDateAsGraphQLString(cashingDateTo) : cashingDateTo,
          first: first,
          offset: offset,
        },
      })
    ).data.financialFlows.map((financialFlow: FinancialFlow) => {
      financialFlow.cashingDate = financialFlow.cashingDate
        ? new Date(financialFlow.cashingDate)
        : financialFlow.cashingDate;
      return financialFlow;
    });
  }

  private static getDateAsGraphQLString(date: Date): string {
    let dateAsGraphQLString: string = date.getFullYear().toString() + "-";
    const month = (date.getMonth() + 1).toString();
    dateAsGraphQLString += month.length == 2 ? month : "0" + month;
    const day = date.getDate().toString();
    dateAsGraphQLString += "-" + (day.length == 2 ? day : "0" + day);
    const hours = date.getHours().toString();
    dateAsGraphQLString += "T" + (hours.length == 2 ? hours : "0" + hours);
    const minutes = date.getMinutes().toString();
    dateAsGraphQLString += ":" + (minutes.length == 2 ? minutes : "0" + minutes);
    const seconds = date.getSeconds().toString();
    dateAsGraphQLString += ":" + (seconds.length == 2 ? seconds : "0" + seconds);
    return dateAsGraphQLString;
  }
}
